// frontend/src/modules/payments/pages/SetupTab.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import { paymentsApi } from '../api';
import type { TenantPaymentGateway, TenantPaymentGatewayInput } from '../types';

const DEFAULT: TenantPaymentGatewayInput = {
  provider: 'stripe',
  publishable_key: '',
  api_secret: '',
  webhook_secret: '',
  account_email: '',
  is_test_mode: true,
};

export function SetupTab() {
  const [loaded, setLoaded] = React.useState<TenantPaymentGateway | null>(null);
  const [webhookUrl, setWebhookUrl] = React.useState<string>('');
  const [form, setForm] = React.useState<TenantPaymentGatewayInput>(DEFAULT);
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    paymentsApi.getGatewayConfig()
      .then(({ config: cfg, webhook_url }) => {
        setLoaded(cfg);
        setWebhookUrl(webhook_url ?? '');
        if (cfg) {
          setForm({
            provider: cfg.provider,
            publishable_key: cfg.publishable_key,
            api_secret: '',
            webhook_secret: '',
            account_email: cfg.account_email ?? '',
            is_test_mode: cfg.is_test_mode,
          });
        }
      })
      .finally(() => setLoading(false));
  }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!form.publishable_key?.trim()) { toast.error('Publishable key is required'); return; }
    setSaving(true);
    try {
      const cfg = await paymentsApi.putGatewayConfig(form);
      setLoaded(cfg);
      setForm((f) => ({ ...f, api_secret: '', webhook_secret: '' }));
      toast.success('Stripe configuration saved');
    } catch (err: any) {
      toast.error('Save failed', { description: err.message });
    } finally {
      setSaving(false);
    }
  }

  function copyWebhookUrl() {
    if (!webhookUrl) return;
    navigator.clipboard.writeText(webhookUrl).then(
      () => toast.success('Webhook URL copied'),
      () => toast.error('Copy failed'),
    );
  }

  if (loading) return <LoadingState variant="inline" />;

  return (
    <form onSubmit={save} className="max-w-3xl space-y-6">
      <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
        <div className="flex items-center gap-3 mb-5">
          <h3 className={VISUAL.dashSection}>Stripe</h3>
          {loaded && (loaded.is_test_mode
            ? <span className={statusPill('warning')}><span className={statusDot('warning')} />Test mode</span>
            : <span className={statusPill('success')}><span className={statusDot('success')} />Live mode</span>)}
        </div>
        <p className="text-[13px] text-muted-foreground leading-relaxed mb-5">
          Each tenant uses their own Stripe account — Worqly never holds your funds.
          Find your keys at <a className="underline hover:text-foreground" href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer">dashboard.stripe.com/apikeys</a>.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="grid gap-1.5">
            <Label>Publishable key<span className="text-destructive ml-0.5">*</span></Label>
            <Input value={form.publishable_key} onChange={(e) => setForm({ ...form, publishable_key: e.target.value })} placeholder="pk_test_..." required />
          </div>
          <div className="grid gap-1.5">
            <Label>Account email</Label>
            <Input type="email" value={form.account_email ?? ''} onChange={(e) => setForm({ ...form, account_email: e.target.value })} placeholder="billing@yourcompany.com" />
          </div>
          <div className="grid gap-1.5 sm:col-span-2">
            <Label>Secret key{loaded?.api_secret_set && <span className="text-[11px] text-muted-foreground ml-2 font-normal">(leave blank to keep existing)</span>}</Label>
            <Input type="password" value={form.api_secret ?? ''} onChange={(e) => setForm({ ...form, api_secret: e.target.value })} placeholder={loaded?.api_secret_set ? '•••••••• keep existing' : 'sk_test_...'} autoComplete="off" />
          </div>
          <div className="grid gap-1.5 sm:col-span-2">
            <Label>Webhook signing secret{loaded?.webhook_secret_set && <span className="text-[11px] text-muted-foreground ml-2 font-normal">(leave blank to keep existing)</span>}</Label>
            <Input type="password" value={form.webhook_secret ?? ''} onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })} placeholder={loaded?.webhook_secret_set ? '•••••••• keep existing' : 'whsec_...'} autoComplete="off" />
            <div className="mt-1">
              <p className="text-[12px] text-muted-foreground">
                In Stripe, add a webhook endpoint pointing at this exact URL — copy it, don't retype it:
              </p>
              <div className="mt-1.5 flex items-center gap-2">
                <code
                  data-testid="stripe-webhook-url"
                  className="flex-1 min-w-0 truncate rounded-md border border-border bg-muted/40 px-2.5 py-1.5 font-mono text-[11.5px] text-foreground"
                >
                  {webhookUrl || '—'}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  onClick={copyWebhookUrl}
                  disabled={!webhookUrl}
                  className="h-8 rounded-full px-3 text-[12px] font-semibold shrink-0"
                >
                  Copy
                </Button>
              </div>
              <p className="text-[12px] text-muted-foreground mt-1.5">
                Then paste the signing secret Stripe shows you (<code className="font-mono text-[11.5px]">whsec_…</code>) into the field above.
                Until both match, Stripe sends events Worqly rejects and paid invoices will not settle.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
        <h3 className={[VISUAL.dashSection, 'mb-2'].join(' ')}>Mode</h3>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_test_mode ?? true}
            onChange={(e) => setForm({ ...form, is_test_mode: e.target.checked })}
            className="mt-1 h-4 w-4 rounded border-border accent-saffron"
          />
          <span className="flex-1">
            <span className="text-[13.5px] font-semibold text-foreground block">Test mode</span>
            <span className="text-[12.5px] text-muted-foreground block mt-0.5">Uncheck once your live keys are in. Test mode safely simulates payments — no real money moves.</span>
          </span>
        </label>
      </section>

      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={saving} className="rounded-full h-11 px-6 text-[14px] font-semibold">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
